import { api } from "./api"

// Account-synced, user-GLOBAL console UI preferences (theme, event-header mode,
// records layout, enter-to-send). Stored as a JSON blob on the caller's
// user_profiles row server-side via /me/console-prefs, so the chrome settings
// follow the user across browsers/devices instead of living only in this
// browser's localStorage.
//
// EVERYTHING here is best-effort. The backend actions ship AFTER this FE (they
// need a quickback compile + deploy), so the GET may 404 and the POST may fail
// until then — in which case we silently fall back to the existing
// localStorage-only behavior. No throws, no error toasts.

export type ConsolePrefs = {
  theme?: "light" | "dark" | "system"
  heroCompact?: boolean
  recordsFullscreen?: boolean
  enterToSend?: boolean
}

// Session-memoized: theme.tsx and shell-prefs.ts both hydrate from this, so
// share a single round-trip. Cleared by invalidateConsolePrefs() (e.g. on sign
// out / sign in) if a caller ever needs a fresh read.
let cached: Promise<ConsolePrefs> | null = null

async function load(): Promise<ConsolePrefs> {
  try {
    const res = await api<{ success: true; prefs: ConsolePrefs | null }>(
      "/api/v1/me/console-prefs",
    )
    return res.prefs ?? {}
  } catch {
    // Not signed in, endpoint not deployed yet, offline, etc. — fall back to
    // local-only prefs silently.
    return {}
  }
}

export function fetchConsolePrefs(): Promise<ConsolePrefs> {
  if (!cached) cached = load()
  return cached
}

export function invalidateConsolePrefs(): void {
  cached = null
}

// Fire-and-forget write-through. Updates the memoized blob optimistically so a
// later fetchConsolePrefs() reflects the change, then POSTs the patch. Never
// throws.
export function pushConsolePrefs(patch: ConsolePrefs): void {
  cached = (cached ?? Promise.resolve({} as ConsolePrefs))
    .then((c) => ({ ...c, ...patch }))
    .catch(() => ({ ...patch }))
  void api("/api/v1/me/console-prefs", { method: "POST", body: patch }).catch(() => {
    // swallow — local state already updated; server sync is best-effort
  })
}
